import React, { ChangeEvent, useState } from 'react'

import { MdmDevice, useDeleteDeviceMutation, useGetAllObjectsQuery } from 'gql/generated/types';
import { Chips, ChipsChangeParams } from 'primereact/chips';
import { useNavigate } from 'react-router-dom';
import InputLabelWrapper from 'components/basic/input-label-wrapper/InputLabelWrapper';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';

type DeviceFormProps = {
  device: MdmDevice | null;
  onSubmit: (device: MdmDevice) => void;
  newForm?: boolean;
}

const defaultDevice: Partial<MdmDevice> = {
  tags: [],
}

const DeviceForm: React.FC<DeviceFormProps> = ({device, onSubmit, newForm = false}) => {
  const [editable, setEditable] = useState(newForm);
  const [deviceState, setDeviceState] = useState<MdmDevice>(device ?? defaultDevice as MdmDevice);
  const navigate = useNavigate();
  const { data: objectData } = useGetAllObjectsQuery();
  const [mutateFunction] = useDeleteDeviceMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device deleted successfully",
        life: 3000,
      });
      navigate(-1);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetAllDevices", "GetObject"],
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setDeviceState(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this device?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        mutateFunction({
          variables: {
            id: deviceState.id!,
          },
        });
      },
    })
  };

  return (
    <div className="flex justify-content-between flex-column gap-5 mx-auto" style={{ maxWidth: "800px" }}>
      <ConfirmPopup />
      <div className="grid p-fluid">
        <InputLabelWrapper labelName="Identifier" col={4}>
          <InputText
            value={deviceState.identifier ?? ""}
            onChange={handleChange}
            className="p-inputtext-sm"
            name="identifier"
            placeholder="Identifier"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName="Object" col={4}>
          <Dropdown
            value={{ title: deviceState.mdmObject?.title, id: deviceState.mdmObject?.id }}
            options={objectData?.allMdmObjects.map(object => ({
              title: object.title,
              id: object.id,
            }))}
            onChange={e => setDeviceState(prev => ({ ...prev, mdmObject: e.value }))}
            optionLabel="title"
            name="mdmObject"
            placeholder="Object"
            className="p-inputtext-sm"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <div className="text-right col-4 mt-2 ">
          {!editable && (
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-text"
              onClick={() => setEditable(true)}
            />
          )}
          {editable && !newForm && (
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-text p-button-danger"
              onClick={handleDelete}
            />
          )}
        </div>
        <InputLabelWrapper labelName="Tags" col={12}>
          <Chips
            value={deviceState.tags ?? []}
            onChange={handleChange}
            className="p-inputtext-sm"
            name="tags"
            placeholder="Tags.."
            disabled={!editable}
          />
        </InputLabelWrapper>
        {!newForm && (
          <>
            <InputLabelWrapper labelName="Created at" col={4}>
              <InputText
                value={new Date(deviceState.createdAt).toUTCString() ?? ""}
                className="p-inputtext-sm"
                name="createdAt"
                disabled
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName="Updated at" col={4}>
              <InputText
                value={new Date(deviceState.modifiedAt).toUTCString() ?? ""}
                className="p-inputtext-sm"
                name="modifiedAt"
                disabled
              />
            </InputLabelWrapper>
          </>
        )}
      </div>
      <div className="flex w-full justify-content-end gap-3">
        {editable && (
          <Button
            onClick={() => onSubmit(deviceState)}
            label={newForm ? "Create" : "Save"}
            className={`p-button-rounded p-button-${newForm ? "success" : "primary"} w-10rem`}
          />
        )}
        {device && !newForm && editable && (
          <Button
            onClick={() => {
              setEditable(false);
              setDeviceState(device ?? {});
            }}
            label="Cancel"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
        {((newForm && !device) || !editable) && (
          <Button
            onClick={() => navigate(-1)}
            label="Back"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
      </div>
    </div>
  );
}

export default DeviceForm